<section class="sav-sc-hero" aria-label="Submit a coupon">
  <div class="sav-site-container">
    <app-breadcrumb [items]="crumbs" />
    <div class="sav-sc-hero-inner">
      <span class="sav-sc-eyebrow">Community Reward</span>
      <h1 class="sav-sc-title">
        Submit a Coupon <app-dev-flag note="Static hero copy (form itself posts to backend)" />
      </h1>
      <p class="sav-sc-subtitle">
        Found a better deal? Share your coupon code with the community and help others save big.
        Every submission is reviewed by our team before it goes live.
      </p>
    </div>
  </div>
</section>

<section class="sav-sc-body" aria-label="Coupon submission form">
  <div class="sav-site-container">
    <div class="sav-sc-card">
      @if (submitted()) {
        <div class="sav-sc-success" role="status">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <div>
            <h2>Thanks for sharing!</h2>
            <p>
              Your coupon has been submitted and is now pending review. We'll publish it once our
              team verifies it.
            </p>
          </div>
        </div>
      } @else {
        <form [formGroup]="couponForm" (ngSubmit)="submitCoupon()" class="sav-sc-form" novalidate>
          <!-- Store selector -->
          <div class="sav-sc-field">
            <span class="sav-sc-label">Store</span>

            @if (selectedStore() && !isPickingStore()) {
              <div class="sav-sc-store-chip">
                <span class="sav-sc-store-logo" aria-hidden="true">
                  <!-- Square: this chip never rounded its logos. The name sits beside it. -->
                  <app-store-logo
                    [logoUrl]="
                      selectedStore()!.logoImagePath
                        ? (selectedStore()!.logoImagePath | assetUrl)
                        : ''
                    "
                    [name]="selectedStore()!.name"
                    shape="square"
                    decorative
                  />
                </span>
                <span class="sav-sc-store-name">{{ selectedStore()!.name }}</span>
                <button type="button" class="sav-sc-store-change" (click)="changeStore()">
                  Change
                </button>
              </div>
            } @else {
              <div class="sav-sc-store-search">
                <input
                  [formControl]="storeSearch"
                  type="text"
                  autocomplete="off"
                  placeholder="Search for a store…"
                />
                @if (storeResults().length > 0) {
                  <ul class="sav-sc-store-results" role="listbox">
                    @for (store of storeResults(); track store.slug) {
                      <li>
                        <button type="button" (click)="pickStore(store)">
                          <span class="sav-sc-store-logo sav-sc-store-logo-sm" aria-hidden="true">
                            <app-store-logo
                              [logoUrl]="store.logoImagePath ? (store.logoImagePath | assetUrl) : ''"
                              [name]="store.name"
                              shape="square"
                              decorative
                            />
                          </span>
                          {{ store.name }}
                        </button>
                      </li>
                    }
                  </ul>
                }
              </div>
              @if (!selectedStore()) {
                <small class="sav-sc-hint">Pick the store this coupon is for.</small>
              }
            }
          </div>

          <div class="sav-sc-field">
            <span class="sav-sc-label">Coupon title</span>
            <input formControlName="title" type="text" placeholder="e.g. 20% off sitewide" />
            @if (couponForm.controls.title.touched && couponForm.controls.title.invalid) {
              <small class="sav-sc-error">Please add a short coupon title.</small>
            }
          </div>

          <div class="sav-sc-field-row">
            <div class="sav-sc-field">
              <span class="sav-sc-label"
                >Coupon code <span class="sav-sc-optional">(optional)</span></span
              >
              <input
                formControlName="code"
                type="text"
                placeholder="e.g. SAVE20 — leave blank for a deal"
              />
            </div>
            <div class="sav-sc-field">
              <span class="sav-sc-label"
                >Expiry <span class="sav-sc-optional">(optional)</span></span
              >
              <input
                formControlName="endDateText"
                type="text"
                placeholder="e.g. 31 Dec 2026 / Ongoing"
              />
            </div>
          </div>

          <div class="sav-sc-field">
            <span class="sav-sc-label">Description</span>
            <textarea
              formControlName="description"
              rows="4"
              placeholder="How does this offer work? Any minimum spend or conditions?"
            ></textarea>
            @if (
              couponForm.controls.description.touched && couponForm.controls.description.invalid
            ) {
              <small class="sav-sc-error"
                >Please add at least 10 characters describing the offer.</small
              >
            }
          </div>

          <button type="submit" class="sav-sc-submit" [disabled]="submitting()">
            {{ submitting() ? 'Submitting…' : 'Submit coupon' }}
          </button>
        </form>
      }
    </div>
  </div>
</section>

<app-big-sale-banner />
